import { getContrastRatio, getReadableTextColor, getRelativeLuminance } from '@/lib/accessibility';
import { createThemeRoles } from '@/lib/palette-workflow';
import { mixHexColors } from '@/lib/studio-color';
import { PLAYGROUND_STARTER } from '@/data/playground-starter';
import type {
  PlaygroundDesign,
  PlaygroundRole,
  PlaygroundRoles,
  PlaygroundState,
  ThemeMode,
  ThemeRoles,
  ThemeSnapshot,
} from '@/types/studio';

export const MAX_THEME_SNAPSHOTS = 12;
export const THEME_ROLES: ReadonlyArray<{
  id: PlaygroundRole;
  label: string;
  group: 'Foundation' | 'Brand' | 'Feedback';
}> = [
  { id: 'background', label: 'Background', group: 'Foundation' },
  { id: 'surface', label: 'Surface', group: 'Foundation' },
  { id: 'text', label: 'Text', group: 'Foundation' },
  { id: 'muted', label: 'Muted text', group: 'Foundation' },
  { id: 'border', label: 'Border', group: 'Foundation' },
  { id: 'primary', label: 'Primary', group: 'Brand' },
  { id: 'onPrimary', label: 'On primary', group: 'Brand' },
  { id: 'secondary', label: 'Secondary', group: 'Brand' },
  { id: 'onSecondary', label: 'On secondary', group: 'Brand' },
  { id: 'accent', label: 'Accent', group: 'Brand' },
  { id: 'onAccent', label: 'On accent', group: 'Brand' },
  { id: 'success', label: 'Success', group: 'Feedback' },
  { id: 'warning', label: 'Warning', group: 'Feedback' },
  { id: 'danger', label: 'Danger', group: 'Feedback' },
];
export const FONT_FAMILIES = {
  sans: 'Inter Variable, Inter, ui-sans-serif, system-ui, sans-serif',
  serif: 'Georgia, Cambria, Times New Roman, serif',
  mono: 'ui-monospace, Cascadia Code, Consolas, monospace',
} as const;
export const PREVIEW_WIDTHS = { desktop: 1100, tablet: 768, mobile: 390 } as const;
export const roleCssName = (role: string) =>
  role.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
export const roleLabel = (role: PlaygroundRole) =>
  THEME_ROLES.find((entry) => entry.id === role)!.label;

export function completeTheme(base: ThemeRoles): PlaygroundRoles {
  const dark = getRelativeLuminance(base.background) < 0.3;
  return {
    ...base,
    muted: mixHexColors(base.text, base.background, 35, 'oklab'),
    border: mixHexColors(base.text, base.background, 50, 'oklab'),
    onPrimary: getReadableTextColor(base.primary),
    onSecondary: getReadableTextColor(base.secondary),
    onAccent: getReadableTextColor(base.accent),
    success: dark ? '#86E5B4' : '#22663F',
    warning: dark ? '#F5C76A' : '#865700',
    danger: dark ? '#FF9BA6' : '#BA3645',
  };
}

export function themesFromPalette(colors: string[]): Record<ThemeMode, PlaygroundRoles> {
  return {
    light: completeTheme(createThemeRoles(colors)),
    dark: completeTheme(createThemeRoles(colors, true)),
  };
}

export function createDefaultPlayground(): PlaygroundState {
  return {
    themes: themesFromPalette(['#6D5DFC', '#D1C8FF', '#FFB98E']),
    mode: 'light',
    template: 'interface',
    radius: 16,
    font: 'sans',
    fontScale: 100,
    density: 'comfortable',
    vision: 'normal',
    viewport: 'fit',
    compare: 'none',
    referenceId: null,
    snapshots: [],
    source: { ...PLAYGROUND_STARTER },
  };
}

export function editThemeRole(
  state: PlaygroundState,
  role: PlaygroundRole,
  color: string,
): PlaygroundState {
  return {
    ...state,
    themes: { ...state.themes, [state.mode]: { ...state.themes[state.mode], [role]: color } },
  };
}

export function getThemeDesign(state: PlaygroundDesign): PlaygroundDesign {
  return {
    themes: { light: { ...state.themes.light }, dark: { ...state.themes.dark } },
    radius: state.radius,
    font: state.font,
    fontScale: state.fontScale,
    density: state.density,
  };
}

export function makeThemeSnapshot(state: PlaygroundState, name: string): ThemeSnapshot {
  return {
    id: crypto.randomUUID(),
    name: name.trim().slice(0, 60) || `Version ${state.snapshots.length + 1}`,
    createdAt: new Date().toISOString(),
    design: getThemeDesign(state),
  };
}

export function restoreThemeSnapshot(
  state: PlaygroundState,
  snapshot: ThemeSnapshot,
): PlaygroundState {
  return { ...state, ...getThemeDesign(snapshot.design) };
}

export function themeDifferences(
  current: PlaygroundDesign,
  reference: PlaygroundDesign,
  mode: ThemeMode,
): PlaygroundRole[] {
  return THEME_ROLES.filter(
    ({ id }) => current.themes[mode][id] !== reference.themes[mode][id],
  ).map(({ id }) => id);
}

export const THEME_CONTRAST_PAIRS: ReadonlyArray<{
  label: string;
  foreground: PlaygroundRole;
  background: PlaygroundRole;
  target: number;
}> = [
  { label: 'Body text', foreground: 'text', background: 'background', target: 4.5 },
  { label: 'Muted text', foreground: 'muted', background: 'background', target: 4.5 },
  { label: 'Text on surface', foreground: 'text', background: 'surface', target: 4.5 },
  { label: 'Muted on surface', foreground: 'muted', background: 'surface', target: 4.5 },
  { label: 'Error on surface', foreground: 'danger', background: 'surface', target: 4.5 },
  { label: 'Primary text', foreground: 'primary', background: 'background', target: 4.5 },
  { label: 'Primary button', foreground: 'onPrimary', background: 'primary', target: 4.5 },
  { label: 'Secondary button', foreground: 'onSecondary', background: 'secondary', target: 4.5 },
  { label: 'Accent label', foreground: 'onAccent', background: 'accent', target: 4.5 },
  { label: 'Input border', foreground: 'border', background: 'surface', target: 3 },
  { label: 'Success text', foreground: 'success', background: 'background', target: 4.5 },
  { label: 'Warning text', foreground: 'warning', background: 'background', target: 4.5 },
  { label: 'Error text', foreground: 'danger', background: 'background', target: 4.5 },
];

export function checkThemeContrast(roles: PlaygroundRoles) {
  return THEME_CONTRAST_PAIRS.map((pair) => {
    const ratio = getContrastRatio(roles[pair.foreground], roles[pair.background]);
    return { ...pair, ratio, pass: ratio >= pair.target };
  });
}

export function playgroundEditGroup(
  previous: PlaygroundState,
  next: PlaygroundState,
): string | undefined {
  if (previous.mode !== next.mode || previous.snapshots !== next.snapshots) return undefined;
  if (previous.source.html !== next.source.html) return 'playground-html';
  if (previous.source.css !== next.source.css) return 'playground-css';
  if (previous.radius !== next.radius) return 'playground-radius';
  if (previous.fontScale !== next.fontScale) return 'playground-type-scale';
  const changed = THEME_ROLES.filter(
    ({ id }) => previous.themes[next.mode][id] !== next.themes[next.mode][id],
  );
  return changed.length === 1 ? `playground-${next.mode}-${changed[0]!.id}` : undefined;
}
